export const ARTA_EDITIONS = ['azure', 'aws', 'multicloud'] as const;
export type ArtaEdition = typeof ARTA_EDITIONS[number];

export const ARTA_FEATURES = [
  'templateManagement',
  'templateAssistant',
  'techDocsGeneration',
] as const;
export type ArtaFeature = typeof ARTA_FEATURES[number];
export type ArtaFeatureFlags = Record<ArtaFeature, boolean>;

const DEFAULT_FEATURES: ArtaFeatureFlags = {
  templateManagement: true,
  templateAssistant: true,
  techDocsGeneration: true,
};

export function parseArtaEdition(value: string | undefined, fallback: ArtaEdition = 'azure'): ArtaEdition {
  const normalized = (value ?? fallback).toLowerCase();
  if (!ARTA_EDITIONS.includes(normalized as ArtaEdition)) {
    throw new Error(`Invalid ARTA edition "${normalized}". Expected azure, aws, or multicloud.`);
  }
  return normalized as ArtaEdition;
}

export function editionIncludesAzure(edition: ArtaEdition): boolean {
  return edition === 'azure' || edition === 'multicloud';
}

export function editionIncludesAws(edition: ArtaEdition): boolean {
  return edition === 'aws' || edition === 'multicloud';
}

export function assertEditionLicensed(edition: ArtaEdition, licensedEdition: ArtaEdition): void {
  if (
    (editionIncludesAzure(edition) && !editionIncludesAzure(licensedEdition)) ||
    (editionIncludesAws(edition) && !editionIncludesAws(licensedEdition))
  ) {
    throw new Error(`ARTA edition "${edition}" is not allowed by the "${licensedEdition}" license entitlement.`);
  }
}

export function resolveFeatureFlags(overrides: Partial<ArtaFeatureFlags> = {}): ArtaFeatureFlags {
  const resolved = { ...DEFAULT_FEATURES };
  for (const feature of ARTA_FEATURES) {
    if (typeof overrides[feature] === 'boolean') resolved[feature] = overrides[feature] as boolean;
  }
  return resolved;
}

export type ArtaCapabilities = {
  edition: ArtaEdition;
  licensedEdition: ArtaEdition;
  azureEnabled: boolean;
  awsEnabled: boolean;
  features: ArtaFeatureFlags;
};

export function resolveArtaCapabilities(input: {
  edition?: string;
  licensedEdition?: string;
  features?: Partial<ArtaFeatureFlags>;
}): ArtaCapabilities {
  const edition = parseArtaEdition(input.edition);
  const licensedEdition = parseArtaEdition(input.licensedEdition, edition);
  assertEditionLicensed(edition, licensedEdition);
  return {
    edition,
    licensedEdition,
    azureEnabled: editionIncludesAzure(edition),
    awsEnabled: editionIncludesAws(edition),
    features: resolveFeatureFlags(input.features),
  };
}
